import { MRT_RowData } from "material-react-table";
import {
    Stack,
    useTheme,
    TextField,
    RadioGroup,
    FormControlLabel,
    Radio,
    FormControl,
    FormLabel,
    Divider,
} from "@mui/material";
import { CustomMRTFilter } from "../mrt.types";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import type { EmptyNullValue } from "../EmptyNullFilter";

export type ScoreFilterValue = {
    min?: number | string;
    max?: number | string;
    emptyNull?: EmptyNullValue;
};

export const ScoreFilter = <T extends MRT_RowData>(mrtProps: CustomMRTFilter<T>) => {
    const theme = useTheme();
    const { t } = useTranslation();

    const initFilterValue = mrtProps.column.getFilterValue() as ScoreFilterValue | EmptyNullValue | undefined;

    // Handle both old EmptyNullValue format and new ScoreFilterValue format
    let initialEmptyNull: string = "all";
    let initialMin = "";
    let initialMax = "";

    if (typeof initFilterValue === "string") {
        // Old format: just "empty" or "notEmpty"
        initialEmptyNull = initFilterValue || "all";
    } else if (initFilterValue && typeof initFilterValue === "object") {
        // New format: ScoreFilterValue object
        initialEmptyNull = initFilterValue.emptyNull || "all";
        initialMin = initFilterValue.min?.toString() || "";
        initialMax = initFilterValue.max?.toString() || "";
    }

    const [emptyNullValue, setEmptyNullValue] = useState<string>(initialEmptyNull);
    const [minValue, setMinValue] = useState<string>(initialMin);
    const [maxValue, setMaxValue] = useState<string>(initialMax);

    // Debounce effect to update filter value
    useEffect(() => {
        if (!mrtProps.column) return;

        const timeoutId = setTimeout(() => {
            const hasMin = minValue !== "" && !isNaN(Number(minValue));
            const hasMax = maxValue !== "" && !isNaN(Number(maxValue));
            const hasEmptyNull = emptyNullValue !== "all";

            const columnId = mrtProps.column.id;
            const onColumnFiltersChange = mrtProps.table.options.onColumnFiltersChange;

            if (!hasMin && !hasMax && !hasEmptyNull) {
                if (onColumnFiltersChange) {
                    onColumnFiltersChange((old) => old.filter((f) => f.id !== columnId));
                }
                return;
            }

            const filterValue: ScoreFilterValue = {};
            if (hasMin) filterValue.min = Number(minValue);
            if (hasMax) filterValue.max = Number(maxValue);
            if (hasEmptyNull) filterValue.emptyNull = emptyNullValue as EmptyNullValue;

            if (onColumnFiltersChange) {
                onColumnFiltersChange((old) => {
                    const filtered = old.filter((f) => f.id !== columnId);
                    filtered.push({ id: columnId, value: filterValue });
                    return filtered;
                });
            }
        }, 500);

        return () => clearTimeout(timeoutId);
    }, [minValue, maxValue, emptyNullValue, mrtProps.column, mrtProps.table]);

    return (
        <Stack gap={theme.spacing(2)} sx={{ padding: theme.spacing(1), minWidth: "200px" }}>
            {/* Empty/Not Empty Filter */}
            <FormControl component="fieldset" size="small">
                <FormLabel component="legend" sx={{ fontSize: "0.875rem", marginBottom: theme.spacing(0.5) }}>
                    {t("grid.filter.score.emptyLabel", "État")}
                </FormLabel>
                <RadioGroup
                    value={emptyNullValue}
                    onChange={(e) => setEmptyNullValue(e.target.value)}
                    aria-label={t("grid.filter.emptyNull.ariaLabel", "Filter by empty or not empty")}
                >
                    <FormControlLabel
                        value="all"
                        control={<Radio size="small" />}
                        label={t("grid.filter.emptyNull.all", "Tous")}
                    />
                    <FormControlLabel
                        value="empty"
                        control={<Radio size="small" />}
                        label={t("grid.filter.emptyNull.empty", "Vide")}
                    />
                    <FormControlLabel
                        value="notEmpty"
                        control={<Radio size="small" />}
                        label={t("grid.filter.emptyNull.notEmpty", "Non vide")}
                    />
                </RadioGroup>
            </FormControl>

            <Divider />

            {/* Range Filter */}
            <Stack gap={theme.spacing(1)}>
                <FormLabel component="legend" sx={{ fontSize: "0.875rem" }}>
                    {t("grid.filter.score.rangeLabel", "Plage de valeurs")}
                </FormLabel>
                <TextField
                    label={t("grid.filter.range.min", "Min")}
                    type="number"
                    value={minValue}
                    onChange={(e) => setMinValue(e.target.value)}
                    variant="outlined"
                    size="small"
                    inputProps={{
                        step: "any",
                    }}
                />
                <TextField
                    label={t("grid.filter.range.max", "Max")}
                    type="number"
                    value={maxValue}
                    onChange={(e) => setMaxValue(e.target.value)}
                    variant="outlined"
                    size="small"
                    inputProps={{
                        step: "any",
                    }}
                />
            </Stack>
        </Stack>
    );
};
